import { User } from 'types';
import dayjs, { Dayjs } from 'dayjs';
import { apiService } from '../services/apiService';
import { getEnrichedWorkplaceUsers } from '../utils/enrichWorkplaceUsers';

// Types for API operations
export interface CreateUserRequest {
	firstName: string;
	lastName: string;
	middleName?: string;
	email: string;
	phone: string;
	gender: string;
	maritalStatus: string;
	dateOfBirth?: Dayjs;
	addressLine1: string;
	addressLine2?: string;
	city: string;
	state: string;
	postcode: string;
	country: string;
	startDate: Dayjs;
	hierarchy: string | number;
	securityRole: string;
	active: boolean;
	separation: boolean;
	separationDate?: Dayjs | null;
	userMustChangePasswordOnNextLogin: boolean;
}

export interface UpdateUserRequest {
	id: number;
	firstName: string;
	lastName: string;
	middleName?: string;
	email: string;
	phone: string;
	gender: string;
	maritalStatus: string;
	dateOfBirth?: Dayjs;
	addressLine1: string;
	addressLine2?: string;
	city: string;
	state: string;
	postcode: string;
	country: string;
	startDate: Dayjs;
	hierarchy: string | number;
	securityRole: string;
	active: boolean;
	separation: boolean;
	separationDate?: Dayjs | null;
	userMustChangePasswordOnNextLogin: boolean;
}

export interface GetUsersParams {
	workplaceId?: number;
	orderBy?: string;
	direction?: 'ASC' | 'DESC';
	limit?: number;
	page?: number;
	active?: boolean;
}

export interface GetUsersResponse {
	data: User[];
	meta: {
		total: number;
		page: number;
		limit: number;
	};
}

// Transform API response to User interface
const transformUserResponse = (apiUser: any): User => {
	// console.log('apiusers', apiUser);
	return {
		id: apiUser.id,
		name: `${apiUser.firstName} ${apiUser.lastName}`,
		firstName: apiUser.firstName,
		lastName: apiUser.lastName,
		hierarchy: apiUser.hierarchy,

		userType: apiUser.userType
			? {
					id: apiUser.userType.id,
					label: apiUser.userType.label,
					active: apiUser.userType?.active,
				}
			: undefined,
		// Convert dates to Dayjs objects
		startDate: apiUser.startDate ? dayjs(apiUser.startDate) : dayjs(),
		dateOfBirth: apiUser.dateOfBirth ? dayjs(apiUser.dateOfBirth) : undefined,
		securityRole: apiUser.securityRole,
		phone: apiUser.phone,
		email: apiUser.email,
		middleName: apiUser.middleName,
		maritalStatus: apiUser.maritalStatus,
		gender: apiUser.gender,
		addressLine1: apiUser.addressLine1,
		addressLine2: apiUser.addressLine2,
		city: apiUser.city,
		state: apiUser.state,
		country: apiUser.country,
		postcode: apiUser.postcode,
		separation: apiUser.separation,
		separationDate: apiUser.separationDate ? dayjs(apiUser.separationDate) : undefined,
		userMustChangePasswordOnNextLogin: apiUser.userMustChangePasswordOnNextLogin,
		userNotes: apiUser.userNotes || [],
	};
};

// Transform create/update request to API format
const transformUserRequest = (user: CreateUserRequest | UpdateUserRequest) => {
	const { active, ...rest} = user;
	const suspended = !active;
	return {
		...rest,
		suspended,
		startDate: user.startDate.format(),
		separationDate: user.separationDate ? user.separationDate: null,
	};
};

/**
 * Get all users with optional filtering and pagination
 */
export const getUsers = async (params: GetUsersParams = {}): Promise<GetUsersResponse> => {
	try {
		const {
			workplaceId,
			orderBy = 'lastName',
			direction = 'ASC',
			limit = 50,
			page = 1,
			active = true,
		} = params;

		const queryParams: any = {
			orderBy,
			direction,
			limit,
			page,
			active: active ? 1 : 0,
			...(workplaceId && { workplace: workplaceId }),
		};

		const endpoint = 'workplace_employees/users';

		let userList: any[];

		if (workplaceId) {
			// Use enriched workplace users (workplace_employees/users + /users merge)
			userList = await getEnrichedWorkplaceUsers(workplaceId);
		} else {
			const response = apiService.responseHandler(
				await apiService.get(endpoint, {
					params: queryParams,
				})
			);
			userList = response?.member || [];
		}

		// Handle different response structures
		const users: User[] = userList.map(transformUserResponse)
			.sort((a: User, b: User) => (a.lastName || '').localeCompare(b.lastName || ''));
		const meta = {
			total: 0,
			page: page,
			limit: limit,
		};
		// console.log("user response", response)
		meta.total = userList.length;

		return {
			data: users,
			meta,
		};
	} catch (error: any) {
		console.error('Failed to fetch users:', error);
		throw new Error(error?.message || 'Failed to fetch users');
	}
};

/**
 * Get a single user by ID
 */
/**
 * Get a single user by ID, with optional pagination for related data (if supported by backend)
 * @param userId - The ID of the user to fetch
 * @param options - Optional pagination options (e.g., for related entities)
 */
export const getUser = async (
	userId: number,
	options?: { page?: number; limit?: number }
): Promise<User> => {
	try {
		const params: any = {};
		if (options?.page !== undefined) params.page = options.page;
		if (options?.limit !== undefined) params.limit = options.limit;

		const response = apiService.responseHandler(
			await apiService.get(`users/${userId}`, { params })
		);
		// console.log('response', response);

		return transformUserResponse(response.data || response);
	} catch (error: any) {
		console.error(`Failed to fetch user ${userId}:`, error);
		throw new Error(error.message || 'Failed to fetch user');
	}
};

/**
 * Create a new user
 */
export const createUser = async (userData: CreateUserRequest): Promise<User> => {
	try {
		const payload = transformUserRequest(userData);
		console.log('Creating user with payload:', payload);

		const response = apiService.responseHandler(await apiService.post('users', payload));

		return transformUserResponse(response.data || response);
	} catch (error: any) {
		console.error('Failed to create user:', error);
		throw new Error(error.message || 'Failed to create user');
	}
};

/**
 * Update an existing user
 */
export const updateUser = async (userData: UpdateUserRequest): Promise<User> => {
	try {
		const { id, ...updatePayload } = userData;
		const payload = transformUserRequest({ ...updatePayload, id });

		const response = apiService.responseHandler(await apiService.put(`users/${id}`, payload));

		return transformUserResponse(response.data || response);
	} catch (error: any) {
		console.error(`Failed to update user ${userData.id}:`, error);
		throw new Error(error.message || 'Failed to update user');
	}
};

/**
 * Delete a user (soft delete)
 */
export const deleteUser = async (userId: number): Promise<void> => {
	try {
		await apiService.delete(`users/${userId}`);
	} catch (error: any) {
		console.error(`Failed to delete user ${userId}:`, error);
		throw new Error(error.message || 'Failed to delete user');
	}
};

/**
 * Toggle user active status
 */
export const toggleUserStatus = async (userId: number, active: boolean): Promise<User> => {
	try {
		const response = apiService.responseHandler(
			await apiService.put(`users/${userId}/status`, { active })
		);

		return transformUserResponse(response.data || response);
	} catch (error: any) {
		console.error(`Failed to toggle user status for ${userId}:`, error);
		throw new Error(error.message || 'Failed to toggle user status');
	}
};

// Export all functions as a service object for easier importing
export const userSettingsService = {
	getUsers,
	getUser,
	createUser,
	updateUser,
	deleteUser,
	toggleUserStatus,
};

export default userSettingsService;
